import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import formatMZN from "../../utils/formatMZN";
import { useTranslation } from "../../i18n/I18nContext";

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const sumForDay = (entries, day) =>
  (entries || []).reduce((sum, item) => {
    const itemDate = new Date(item.date);
    return sameDay(itemDate, day) ? sum + parseFloat(item.sum || 0) : sum;
  }, 0);

const buildWeekData = (entries, dayNames) => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const points = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    points.push({ x: dayNames[d.getDay()], y: sumForDay(entries, d) });
  }
  return points;
};

const buildMonthData = (entries) => {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const points = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), day);
    points.push({ x: String(day), y: sumForDay(entries, d) });
  }
  return points;
};

const buildYearData = (entries, monthNames) => {
  const now = new Date();
  const points = [];
  for (let month = 0; month < 12; month += 1) {
    const sum = (entries || []).reduce((total, item) => {
      const itemDate = new Date(item.date);
      return itemDate.getFullYear() === now.getFullYear() && itemDate.getMonth() === month
        ? total + parseFloat(item.sum || 0)
        : total;
    }, 0);
    points.push({ x: monthNames[month], y: sum });
  }
  return points;
};

const PAYMENTS_COLOR = "#487FFF";
const DEBTS_COLOR = "#FF9F29";

const EarningStatistic = ({ dashboardData, loading }) => {
  const { t } = useTranslation();
  const data = dashboardData || {};
  const [period, setPeriod] = useState("month");

  const dayNames = useMemo(
    () => [
      t('dashboard.day.sun'), t('dashboard.day.mon'), t('dashboard.day.tue'),
      t('dashboard.day.wed'), t('dashboard.day.thu'), t('dashboard.day.fri'),
      t('dashboard.day.sat'),
    ],
    [t],
  );
  const monthNames = useMemo(
    () => [
      t('dashboard.month.jan'), t('dashboard.month.feb'), t('dashboard.month.mar'),
      t('dashboard.month.apr'), t('dashboard.month.may'), t('dashboard.month.jun'),
      t('dashboard.month.jul'), t('dashboard.month.aug'), t('dashboard.month.sep'),
      t('dashboard.month.oct'), t('dashboard.month.nov'), t('dashboard.month.dec'),
    ],
    [t],
  );

  const PERIODS = useMemo(
    () => ({
      week: { label: t('dashboard.earnings.period.week'), build: (entries) => buildWeekData(entries, dayNames) },
      month: { label: t('dashboard.earnings.period.month'), build: buildMonthData },
      year: { label: t('dashboard.earnings.period.year'), build: (entries) => buildYearData(entries, monthNames) },
    }),
    [t, dayNames, monthNames],
  );

  const paymentsData = useMemo(
    () => PERIODS[period].build(data.paymentsByDate),
    [data.paymentsByDate, period, PERIODS],
  );
  const debtsData = useMemo(
    () => PERIODS[period].build(data.overduePaymentsByDate),
    [data.overduePaymentsByDate, period, PERIODS],
  );

  const totalPayments = useMemo(
    () => paymentsData.reduce((sum, point) => sum + point.y, 0),
    [paymentsData],
  );
  const totalDebts = useMemo(
    () => debtsData.reduce((sum, point) => sum + point.y, 0),
    [debtsData],
  );

  const chartOptions = {
    legend: { show: false },
    chart: {
      type: "area",
      width: "100%",
      height: 260,
      toolbar: { show: false },
      padding: { left: 0, right: 0, top: 0, bottom: 0 },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [PAYMENTS_COLOR, DEBTS_COLOR],
      lineCap: "round",
    },
    grid: {
      show: true,
      borderColor: "#D1D5DB",
      strokeDashArray: 1,
      position: "back",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: -20, right: 0, bottom: -10, left: 0 },
    },
    fill: {
      type: "gradient",
      colors: [PAYMENTS_COLOR, DEBTS_COLOR],
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [undefined, `${DEBTS_COLOR}00`],
        inverseColors: false,
        opacityFrom: [0.4, 0.3],
        opacityTo: [0.3, 0.1],
        stops: [0, 100],
      },
    },
    markers: {
      colors: [PAYMENTS_COLOR, DEBTS_COLOR],
      strokeWidth: 3,
      size: 0,
      hover: { size: 8 },
    },
    xaxis: {
      categories: paymentsData.map((point) => point.x),
      tooltip: { enabled: false },
      labels: { style: { fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        formatter: (value) => `${Math.round(value / 1000)}k`,
        style: { fontSize: "12px" },
      },
    },
    tooltip: {
      y: { formatter: (value) => formatMZN(value) },
    },
  };

  const chartSeries = [
    { name: t('dashboard.earnings.seriesPayments'), data: paymentsData.map((point) => point.y) },
    { name: t('dashboard.earnings.seriesDebts'), data: debtsData.map((point) => point.y) },
  ];

  return (
    <>
      <div className='col-xxl-12'>
        <div className='card h-100'>
          <div className='card-header'>
            <div className='d-flex align-items-center flex-wrap gap-2 justify-content-between'>
              <h6 className='mb-2 fw-bold text-lg mb-0'>{t('dashboard.earnings.title')}</h6>
              <select
                className='form-select form-select-sm w-auto bg-base border-0 text-secondary-light'
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {Object.entries(PERIODS).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className='card-body p-24'>
            <ul className='d-flex flex-wrap align-items-center justify-content-center my-3 gap-3'>
              <li className='d-flex align-items-center gap-2'>
                <span className='w-12-px h-8-px rounded-pill bg-primary-600' />
                <span className='text-secondary-light text-sm fw-semibold'>
                  {t('dashboard.earnings.totalPaid')}:{" "}
                  <span className='text-primary-light fw-bold'>
                    {loading ? "..." : formatMZN(totalPayments)}
                  </span>
                </span>
              </li>
              <li className='d-flex align-items-center gap-2'>
                <span className='w-12-px h-8-px rounded-pill bg-warning-600' />
                <span className='text-secondary-light text-sm fw-semibold'>
                  {t('dashboard.earnings.totalDebt')}:{" "}
                  <span className='text-primary-light fw-bold'>
                    {loading ? "..." : formatMZN(totalDebts)}
                  </span>
                </span>
              </li>
            </ul>
            <div id='enrollmentChart' className='apexcharts-tooltip-style-1'>
              <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type='area'
                height={260}
                width={"100%"}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EarningStatistic;
